import Link from "next/link";
import { LeerZustand } from "../leerzustand";
import { breiteKlasse, cx, prozent } from "./breite";
import { KopfLink } from "./kopf-link";
import { ZustandspilleLink } from "../zustandspille";

/** Segmentfarben als feste, vollständige Klassenketten (Theme-Tokens, nie zusammengesetzt). */
export type TeilungFarbe = "neutral" | "hell" | "primaer" | "mittel" | "ok";

const BALKEN_FARBE: Record<TeilungFarbe, string> = {
  neutral: "bg-surface-2",
  hell: "bg-primary-200",
  primaer: "bg-primary",
  mittel: "bg-primary-400",
  ok: "bg-status-ok",
};

const PUNKT_FARBE: Record<TeilungFarbe, string> = {
  neutral: "shrink-0 size-2.5 inline-block bg-surface-2 rounded-xs me-2.5",
  hell: "shrink-0 size-2.5 inline-block bg-primary-200 rounded-xs me-2.5",
  primaer: "shrink-0 size-2.5 inline-block bg-primary rounded-xs me-2.5",
  mittel: "shrink-0 size-2.5 inline-block bg-primary-400 rounded-xs me-2.5",
  ok: "shrink-0 size-2.5 inline-block bg-status-ok rounded-xs me-2.5",
};

/** Eine Zustandszahl mit Ziel (z. B. „3 blockiert“ → gefiltertes Brett). Nur Zahlen größer 0 werden gezeigt. */
export interface TeilungZustand {
  anzahl: number;
  href: string;
}

export interface TeilungSegment {
  label: string;
  wert: number;
  farbe: TeilungFarbe;
  /** Ziel der Listenzeile (z. B. gefiltertes Brett); fehlt es, ist die Zeile Text. Nur geprüfte interne Pfade. */
  href?: string;
  /** Zustandspillen der Zeile (M-015 AV1), links der Zahl. */
  blockiert?: TeilungZustand;
  ueberfaellig?: TeilungZustand;
}

const ZAHL = new Intl.NumberFormat("de-DE");

/**
 * Teilung mit Linkliste (M-004 B2 c, Design-Guide `fortschritt.md`): EIN Balken aus bis zu fünf Segmenten (Anteil am
 * Gesamt) mit darunterstehender Liste „Label … Zahl“; jede Listenzeile kann ein Link (Absprung aufs gefilterte Brett)
 * sein. Anders als `SegmentStatusKarte` ohne Schwere-Farben: die Segmente sind Arbeitsstände, keine Risikostufen.
 * Segmente mit Wert 0 entfallen im Balken und bleiben in der Liste. Ohne Bestand: Leerzustand statt Balken.
 */
export function TeilungMitLinkliste({
  id,
  titel,
  untertitel,
  segmente,
  link,
  leer,
  zustand,
  fuss,
}: {
  id: string;
  titel: string;
  untertitel?: string;
  segmente: TeilungSegment[];
  link?: { href: string; text: string };
  leer: { titel: string; text: string; aktion?: { href: string; text: string } };
  /** Kopfzeile „Hängt gerade: n blockiert · n überfällig“ über dem Balken (M-015 AV1); Pillen nur bei Zahl > 0. */
  zustand?: { blockiert?: TeilungZustand; ueberfaellig?: TeilungZustand };
  /** Zeile unter der Liste (z. B. die KI-Einschätzung „Wo hängt es?“, M-015 AV2); ohne Bestand entfällt sie. */
  fuss?: React.ReactNode;
}) {
  const titelId = `${id}-titel`;
  const summe = segmente.reduce((s, x) => s + x.wert, 0);
  const blockiert = zustand?.blockiert && zustand.blockiert.anzahl > 0 ? zustand.blockiert : null;
  const ueberfaellig = zustand?.ueberfaellig && zustand.ueberfaellig.anzahl > 0 ? zustand.ueberfaellig : null;
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-actions-status-progress/ui-components-cat-progress-indicators/multi-bar-progress-with-breakdown-list (Hülle: data-display/data-display-sub-analytics-kpis/data-display-cat-progress-cards/goal-progress-bar-card) */}
      <section id={id} aria-labelledby={titelId} className="h-full flex flex-col bg-card border border-card-line shadow-xs rounded-xl overflow-hidden">
        <div className="p-4 pb-0">
          <div className="relative">
            <div className="pe-24">
              <h2 id={titelId} className="font-medium text-sm text-foreground">
                {titel}
              </h2>
              {untertitel && <p className="text-sm text-muted-foreground-1">{untertitel}</p>}
            </div>
            {link && (
              <div className="absolute top-1/2 inset-e-0 -translate-y-1/2">
                <KopfLink href={link.href} text={link.text} />
              </div>
            )}
          </div>
        </div>

        {summe === 0 ? (
          <LeerZustand titel={leer.titel} text={leer.text} aktion={leer.aktion} />
        ) : (
          <div className="p-4 space-y-4">
            {(blockiert || ueberfaellig) && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground-1">Hängt gerade:</span>
                {blockiert && (
                  <ZustandspilleLink ton="gefahr" href={blockiert.href}>
                    {ZAHL.format(blockiert.anzahl)} blockiert
                  </ZustandspilleLink>
                )}
                {ueberfaellig && (
                  <ZustandspilleLink ton="ueberfaellig" href={ueberfaellig.href}>
                    {ZAHL.format(ueberfaellig.anzahl)} überfällig
                  </ZustandspilleLink>
                )}
              </div>
            )}
            <div className="flex gap-x-1 w-full h-2.5 rounded-full overflow-hidden">
              {segmente
                .filter((s) => s.wert > 0)
                .map((s) => {
                  const p = prozent(s.wert, summe);
                  return (
                    <div
                      key={s.label}
                      className={cx("flex flex-col justify-center overflow-hidden text-xs text-primary-foreground text-center whitespace-nowrap", BALKEN_FARBE[s.farbe], breiteKlasse(p))}
                      role="progressbar"
                      aria-label={`${s.label}: ${ZAHL.format(s.wert)}`}
                      aria-valuenow={p}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  );
                })}
            </div>
            <ul>
              {segmente.map((s) => (
                <li key={s.label} className="relative py-2 flex items-center justify-between gap-x-3">
                  <div className="flex items-center min-w-0">
                    <span className={PUNKT_FARBE[s.farbe]} aria-hidden="true" />
                    {s.href ? (
                      <Link className="text-sm text-foreground hover:text-primary focus:outline-hidden focus:text-primary after:absolute after:inset-0" href={s.href}>
                        {s.label}
                      </Link>
                    ) : (
                      <span className="text-sm text-foreground">{s.label}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-1.5">
                    {s.blockiert && s.blockiert.anzahl > 0 && (
                      <ZustandspilleLink ton="gefahr" href={s.blockiert.href}>
                        {ZAHL.format(s.blockiert.anzahl)} blockiert
                      </ZustandspilleLink>
                    )}
                    {s.ueberfaellig && s.ueberfaellig.anzahl > 0 && (
                      <ZustandspilleLink ton="ueberfaellig" href={s.ueberfaellig.href}>
                        {ZAHL.format(s.ueberfaellig.anzahl)} überfällig
                      </ZustandspilleLink>
                    )}
                    <span className="w-7 text-end text-sm text-muted-foreground-1 tabular-nums">{ZAHL.format(s.wert)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {summe > 0 && fuss && <div className="px-4 py-3 border-t border-card-footer">{fuss}</div>}
      </section>
    </>
  );
}
